// Ported from deexoptions-mobile-app/utils/notificationLink.ts.
//
// The mobile version reads `link.mobile` and opens EXTERNAL urls in an in-app browser.
// This one reads `link.web` and hands INTERNAL routes to react-router instead — the two
// clients do not share a route table, which is exactly why the admin form fills both.

import type { NavigateFunction } from "react-router-dom";

/**
 * The optional CTA an admin attaches to an advanced notification (pop-up, slider or
 * emergency alert). Returned by the backend exactly as stored, and `null` when not set.
 */
export type NotificationLink = {
  type?: "INTERNAL" | "EXTERNAL" | string | null;
  /** INTERNAL: the web route to navigate to. This is the one we use. */
  web?: string | null;
  /** INTERNAL: the in-app route. Unused here; kept so the shape matches the API. */
  mobile?: string | null;
  /** EXTERNAL: an absolute http(s) url. */
  url?: string | null;
} | null;

const isExternal = (link: NotificationLink) =>
  String(link?.type ?? "").toUpperCase() === "EXTERNAL";

/**
 * `http(s)` only. The admin form enforces this, but the client must not trust it: this is
 * what stops a `javascript:` or `data:` url authored in the admin from ever reaching the
 * browser. It matters more here than on native, so do not drop it.
 */
const safeExternalUrl = (url?: string | null) => {
  const value = (url ?? "").trim();
  if (!/^https?:\/\//i.test(value)) return null;
  return value;
};

/**
 * Routes such as `buy/crypto` and `/buy-crypto` both come out of the admin form, so
 * normalise to the single leading slash react-router expects.
 */
const safeInternalRoute = (route?: string | null) => {
  const value = (route ?? "").trim();
  if (!value) return null;
  // Anything carrying a scheme is not an in-app route; refuse rather than route to it.
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return null;
  return value.startsWith("/") ? value : `/${value}`;
};

/**
 * Whether this notification has a CTA worth showing. Drives whether a slide is clickable
 * and whether the pop-up renders its button, so a notification with a malformed link never
 * looks interactive.
 */
export const hasNotificationLink = (link: NotificationLink): boolean => {
  if (!link?.type) return false;
  return isExternal(link)
    ? Boolean(safeExternalUrl(link.url))
    : Boolean(safeInternalRoute(link.web));
};

/**
 * Acts on a notification's CTA: EXTERNAL opens a new tab, INTERNAL navigates in-app. Never
 * throws — a malformed route from the admin should be inert, not a crash on the dashboard.
 */
export const openNotificationLink = (
  link: NotificationLink,
  navigate: NavigateFunction
) => {
  try {
    if (isExternal(link)) {
      const url = safeExternalUrl(link?.url);
      if (!url) return;
      // noopener/noreferrer: the destination is admin-authored, not necessarily ours.
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    const route = safeInternalRoute(link?.web);
    if (!route) return;
    navigate(route);
  } catch (error) {
    // A bad admin-authored route must not take the dashboard down with it.
    console.warn("[notificationLink] could not open link", error);
  }
};
